import type { DataProvider } from 'ra-core';

interface LaravelDataProviderOptions {
    apiUrl: string;
    httpClient?: (url: string, options?: RequestInit) => Promise<Response>;
}

function defaultHttpClient(url: string, options?: RequestInit): Promise<Response> {
    return fetch(url, options);
}

export function laravelDataProvider(options: LaravelDataProviderOptions): DataProvider {
    const { apiUrl, httpClient = defaultHttpClient } = options;

    function getHeaders(): HeadersInit {
        const token = localStorage.getItem('auth_token');
        return {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
    }

    async function request(url: string, init?: RequestInit) {
        const response = await httpClient(url, {
            ...init,
            headers: { ...getHeaders(), ...init?.headers },
        });

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || `HTTP ${response.status}`);
        }

        return response.json();
    }

    function buildQueryString(params: Record<string, string | number | undefined>): string {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined) {
                search.set(key, String(value));
            }
        }
        const str = search.toString();
        return str ? `?${str}` : '';
    }

    return {
        async getList(resource, params) {
            const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
            const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };

            const qs = buildQueryString({
                page,
                per_page: perPage,
                sort: field,
                order: order?.toLowerCase(),
                ...params.filter,
            });

            const json = await request(`${apiUrl}/${resource}${qs}`);
            const data = json.data ?? json;

            return {
                data: Array.isArray(data) ? data : [],
                total: json.meta?.total ?? json.total ?? data.length,
            };
        },

        async getOne(resource, params) {
            const json = await request(`${apiUrl}/${resource}/${params.id}`);
            return { data: json.data ?? json };
        },

        async create(resource, params) {
            const json = await request(`${apiUrl}/${resource}`, {
                method: 'POST',
                body: JSON.stringify(params.data),
            });
            return { data: json.data ?? json };
        },

        async update(resource, params) {
            const json = await request(`${apiUrl}/${resource}/${params.id}`, {
                method: 'PUT',
                body: JSON.stringify(params.data),
            });
            return { data: json.data ?? json };
        },

        async delete(resource, params) {
            await request(`${apiUrl}/${resource}/${params.id}`, {
                method: 'DELETE',
            });
            return { data: { id: params.id } as any };
        },

        async getMany(_resource, _params) {
            throw new Error('getMany not implemented');
        },

        async getManyReference(_resource, _params) {
            throw new Error('getManyReference not implemented');
        },

        async updateMany(_resource, _params) {
            throw new Error('updateMany not implemented');
        },

        async deleteMany(resource, params) {
            await Promise.all(
                params.ids.map((id) =>
                    request(`${apiUrl}/${resource}/${id}`, { method: 'DELETE' }),
                ),
            );
            return { data: params.ids };
        },
    };
}

export default laravelDataProvider;
